import type { IdFactory, ItineraryDay, ItineraryEditor, ItineraryNode, ItinerarySegment, TimeSlot } from "../types";

const slots: TimeSlot[] = ["morning", "afternoon", "evening"];
const dayMillis = 86_400_000;

/** 清理编辑后失效的派生住宿引用，允许逐步编排且不改写完整车次事实。 */
export function normalizeRailwayOvernightStays<T extends Pick<ItineraryEditor, "days" | "segments">>(editor: T): T {
  const validIds = new Set(editor.days.flatMap((day) => day.nodes
    .filter((node) => node.nodeType === "stay_area" && node.stayAreaId && node.title.trim() === "火车上")
    .map((node) => node.id)));
  return { ...editor, segments: editor.segments.map((segment) => {
    const source = segment.sourcePayload;
    if (!source?.railwayOvernight) return segment;
    return { ...segment, sourcePayload: { ...source, railwayOvernight: segment.transportMode === "rail"
      ? { stayNodeIds: [...new Set(source.railwayOvernight.stayNodeIds.filter((id) => validIds.has(id)))] }
      : null } };
  }) };
}

/** 返回班次跨越的日历日数，不使用执行机器时区。 */
export function railwayOvernightDays(departure?: string | null, arrival?: string | null): number {
  if (!departure || !arrival) return 0;
  const delta = (Date.parse(`${arrival.slice(0, 10)}T00:00:00Z`) - Date.parse(`${departure.slice(0, 10)}T00:00:00Z`)) / dayMillis;
  return Number.isInteger(delta) && delta > 0 ? delta : 0;
}

/** 日期顺延保留本地钟点与原始时区后缀。 */
function shift(value: string | null | undefined, count: number): typeof value {
  if (!value || !count) return value;
  return new Date(Date.parse(`${value.slice(0, 10)}T00:00:00Z`) + count * dayMillis).toISOString().slice(0, 10) + value.slice(10);
}

/** 顺延计划时刻与主备，原始查询快照保持原样。 */
export function shiftRailwayPlanDates(segment: ItinerarySegment, count: number): void {
  segment.departureAt = shift(segment.departureAt, count);
  segment.arrivalAt = shift(segment.arrivalAt, count);
  const payload = segment.sourcePayload;
  if (!payload) return;
  const shifted = (selection: NonNullable<typeof payload.selected>) => ({
    ...selection, departureAt: shift(selection.departureAt, count), arrivalAt: shift(selection.arrivalAt, count),
  });
  if (payload.selected) payload.selected = shifted(payload.selected);
  payload.backups = payload.backups.map(shifted);
  if (payload.railwayTransfer) payload.railwayTransfer = { ...payload.railwayTransfer,
    plans: payload.railwayTransfer.plans.map((plan) => ({ ...plan, legs: plan.legs.map(shifted) })),
  };
}

/** 以实际钟点确定车站所属时段。 */
function timeSlot(value?: string | null): TimeSlot {
  const hour = Number(value?.slice(11, 13));
  return hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
}

/** 每天实际的住宿节点参与显示，完整车次的相邻性计算跳过这些在途标记。 */
export function railwayOvernightStayIds(segments: ItinerarySegment[]): Set<string> {
  return new Set(segments.flatMap((segment) => segment.sourcePayload?.railwayOvernight?.stayNodeIds ?? []));
}

/** 将一条跨夜铁路按自然日放置住宿首尾，车次与主备仍由原路线唯一保存。 */
export function splitInsertedOvernightRailway(editor: ItineraryEditor, segmentId: string, stayAreaId: string, idFactory: IdFactory, insertedSegmentIds: ReadonlySet<string> = new Set([segmentId])): ItineraryEditor {
  return splitRailwayDays(editor, segmentId, stayAreaId, idFactory, insertedSegmentIds);
}

/** 赶不上同日班次时保留原夜宿，次日从同一住宿点接入新火车。 */
export function deferInsertedRailwayToNextDay(editor: ItineraryEditor, segmentId: string, idFactory: IdFactory): ItineraryEditor {
  const rail = editor.segments.find((segment) => segment.id === segmentId)!;
  const day = editor.days.find((item) => item.id === rail.startDayId)!;
  const stay = day.nodes.filter((node) => node.nodeType === "stay_area" && node.stayAreaId).at(-1);
  const stayAreaId = day.stayAreaId || stay?.stayAreaId;
  const title = day.stayAreaName || stay?.title;
  if (!stayAreaId || !title) throw new Error("按现在的安排，这班火车要第二天才能出发。请先填好出发前一晚住在哪里。");
  return splitRailwayDays(editor, segmentId, stayAreaId, idFactory, new Set(), title);
}

/** 按原路线切开跨日边界；等待发车和乘车过夜共用日期与后续事实顺延。 */
function splitRailwayDays(editor: ItineraryEditor, segmentId: string, stayAreaId: string, idFactory: IdFactory, insertedSegmentIds: ReadonlySet<string>, waitingStayTitle?: string): ItineraryEditor {
  const next = structuredClone(editor);
  const rail = next.segments.find((segment) => segment.id === segmentId)!;
  const waiting = waitingStayTitle !== undefined;
  const count = waiting ? 1 : railwayOvernightDays(rail.departureAt, rail.arrivalAt);
  if (!count) return editor;
  if (next.days.length + count > 30) throw new Error("加上这趟火车后，行程会超过30天。请先调整行程天数，再添加火车。");
  if (!stayAreaId) throw new Error("住宿选项里没有可用的“火车上”。请联系管理员添加或启用这个选项，再添加火车。");
  const dayIndex = next.days.findIndex((day) => day.id === rail.startDayId);
  const day = next.days[dayIndex];
  const origin = day.nodes.find((node) => node.id === rail.fromNodeId)!;
  const destination = day.nodes.find((node) => node.id === rail.toNodeId)!;
  // 使用真实路线的先后关系，不能用旧时段把到站后的活动排回出发站前。
  const successors = new Map(next.segments.filter((segment) => segment.startDayId === day.id && segment.endDayId === day.id
    && (segment.majorId ?? null) === (rail.majorId ?? null)).map((segment) => [segment.fromNodeId, segment.toNodeId]));
  const tailIds = new Set<string>();
  let cursor: string | undefined = waiting ? origin.id : destination.id;
  while (cursor && !tailIds.has(cursor)) { tailIds.add(cursor); cursor = successors.get(cursor); }
  const originalStay = waiting ? day.nodes.findLast((node) => node.nodeType === "stay_area" && node.stayAreaId === stayAreaId) : undefined;
  // 原夜宿属于等待日，不能跟随到站后的活动再次出现在目的地。
  if (originalStay) tailIds.delete(originalStay.id);
  const sharedTailCopies = new Map<string, string>();
  if (rail.majorId) for (const nodeId of [...tailIds]) {
    if (nodeId === destination.id || !next.segments.some((segment) => segment.majorId !== rail.majorId
      && (segment.fromNodeId === nodeId || segment.toNodeId === nodeId))) continue;
    const original = day.nodes.find((node) => node.id === nodeId)!;
    const copy = { ...structuredClone(original), id: idFactory() };
    day.nodes.push(copy);
    sharedTailCopies.set(nodeId, copy.id);
    tailIds.delete(nodeId);
    tailIds.add(copy.id);
    for (const segment of next.segments) if (segment.majorId === rail.majorId) {
      if (segment.fromNodeId === nodeId) segment.fromNodeId = copy.id;
      if (segment.toNodeId === nodeId) segment.toNodeId = copy.id;
    }
  }
  const tail = [...tailIds].map((id) => day.nodes.find((node) => node.id === id)!).filter(Boolean);
  const arrivalDay: ItineraryDay = { ...structuredClone(day), id: idFactory(), nodes: tail, parallelTracks: null };
  day.nodes = day.nodes.filter((node) => !tailIds.has(node.id));
  if (!waiting) {
    origin.timeSlot = timeSlot(rail.departureAt);
    destination.timeSlot = timeSlot(rail.arrivalAt);
  }
  for (const node of waiting ? [] : tail) {
    if (slots.indexOf(node.timeSlot) < slots.indexOf(destination.timeSlot)) node.timeSlot = destination.timeSlot;
  }
  const stayIds: string[] = [];
  const stay = (slot: TimeSlot): ItineraryNode => {
    const node: ItineraryNode = { id: idFactory(), title: waitingStayTitle ?? "火车上", nodeType: "stay_area", stayAreaId, timeSlot: slot, sortOrder: 1 };
    stayIds.push(node.id);
    return node;
  };
  if (!waiting || day.nodes.at(-1)?.nodeType !== "stay_area" || day.nodes.at(-1)?.stayAreaId !== stayAreaId) day.nodes.push(stay("evening"));
  day.stayAreaId = stayAreaId;
  day.stayAreaName = waitingStayTitle ?? "火车上";
  const added: ItineraryDay[] = [];
  for (let offset = 1; offset < count; offset += 1) added.push({
    id: idFactory(), dayNumber: 0, isFullDay: true, stayAreaId, stayAreaName: "火车上", nodes: [stay("morning"), stay("evening")],
  });
  arrivalDay.nodes.unshift(stay("morning"));
  added.push(arrivalDay);
  const originalTracks = day.parallelTracks?.tracks;
  if (originalTracks?.length) {
    for (const item of [day, arrivalDay]) {
      const ids = new Set(item.nodes.map((node) => node.id));
      item.parallelTracks = { version: 1, tracks: originalTracks.flatMap((track) => {
        let trackIds = item === arrivalDay && track.majorId === rail.majorId
          ? track.nodeIds.map((id) => sharedTailCopies.get(id) ?? id) : track.nodeIds;
        if (waiting && item === arrivalDay && track.majorId === rail.majorId && originalStay) trackIds = trackIds.filter((id) => id !== originalStay.id);
        const nodeIds = trackIds.filter((id) => ids.has(id)
          && !(item === day && track.majorId === rail.majorId && sharedTailCopies.has(id)));
        if (!nodeIds.length) return [];
        if (track.majorId === rail.majorId || (waiting && item === day)) {
          if (item === day) {
            if (nodeIds.at(-1) !== day.nodes.at(-1)!.id) nodeIds.push(day.nodes.at(-1)!.id);
          } else nodeIds.unshift(arrivalDay.nodes[0].id);
        }
        let segmentIds = track.segmentIds.filter((id) => {
            const segment = next.segments.find((candidate) => candidate.id === id);
            return segment && ids.has(segment.fromNodeId) && ids.has(segment.toNodeId);
          });
        if (waiting) segmentIds = nodeIds.slice(0, -1).map((fromNodeId, index) => {
          const toNodeId = nodeIds[index + 1];
          const existing = next.segments.find((segment) => segment.majorId === track.majorId && segment.fromNodeId === fromNodeId && segment.toNodeId === toNodeId);
          if (existing) return existing.id;
          const id = idFactory();
          next.segments.push({ id, startDayId: item.id, endDayId: item.id, fromNodeId, toNodeId,
            majorId: track.majorId, routeSeq: next.segments.length + 1, routeType: "charter_bus", transportMode: "bus", sourceType: "pending" });
          return id;
        });
        return [{ ...track, id: item === day ? track.id : idFactory(), nodeIds, segmentIds }];
      }) };
    }
  }
  // 原前序已改为到当晚住宿，不能继续跨日直连新出发站或折返原夜宿。
  if (waiting) next.segments = next.segments.filter((segment) => !((segment.majorId ?? null) === (rail.majorId ?? null)
    && ((day.nodes.some((node) => node.id === segment.fromNodeId) && segment.toNodeId === origin.id)
      || (tailIds.has(segment.fromNodeId) && segment.toNodeId === originalStay?.id))));
  const shiftedNodeIds = new Set([...tailIds, ...next.days.slice(dayIndex + 1).flatMap((item) => item.nodes.map((node) => node.id))]);
  for (const segment of next.segments) if (!waiting && insertedSegmentIds.has(segment.id)) {
    shiftedNodeIds.delete(segment.fromNodeId);
    shiftedNodeIds.delete(segment.toNodeId);
  }
  for (const node of [...tail, ...next.days.slice(dayIndex + 1).flatMap((item) => item.nodes)]) {
    if (!shiftedNodeIds.has(node.id)) continue;
    node.planStartAt = shift(node.planStartAt, count);
    node.planEndAt = shift(node.planEndAt, count);
  }
  for (const segment of next.segments) {
    if (insertedSegmentIds.has(segment.id) || !shiftedNodeIds.has(segment.toNodeId)) continue;
    shiftRailwayPlanDates(segment, count);
  }
  next.days.splice(dayIndex + 1, 0, ...added);
  for (const [index, item] of next.days.entries()) {
    item.dayNumber = index + 1;
    item.dayDate = shift(next.startDate, index);
    item.nodes.sort((a, b) => slots.indexOf(a.timeSlot) - slots.indexOf(b.timeSlot));
    for (const slot of slots) item.nodes.filter((node) => node.timeSlot === slot).forEach((node, order) => { node.sortOrder = order + 1; });
  }
  next.endDate = shift(next.startDate, next.days.length - 1);
  const nodeDays = new Map(next.days.flatMap((item) => item.nodes.map((node) => [node.id, item.id])));
  for (const segment of next.segments) {
    segment.startDayId = nodeDays.get(segment.fromNodeId)!;
    segment.endDayId = nodeDays.get(segment.toNodeId)!;
  }
  if (!waiting) rail.sourcePayload = { version: 1, backups: [], ...rail.sourcePayload, railwayOvernight: { stayNodeIds: stayIds } };
  return next;
}

/** 编辑器按天展示同一铁路段的连接；点击后仍编辑完整站对。 */
export function railwayDaySegments(editor: ItineraryEditor): ItinerarySegment[] {
  editor = normalizeRailwayOvernightStays(editor);
  const nodeDays = new Map(editor.days.flatMap((day) => day.nodes.map((node) => [node.id, day.id])));
  return editor.segments.flatMap((segment) => {
    const stays = segment.sourcePayload?.railwayOvernight?.stayNodeIds;
    if (!stays?.length) return [segment];
    const chain = [segment.fromNodeId, ...stays, segment.toNodeId];
    return chain.slice(0, -1).flatMap((fromNodeId, index) => {
      const toNodeId = chain[index + 1];
      const startDayId = nodeDays.get(fromNodeId);
      const endDayId = nodeDays.get(toNodeId);
      return startDayId && startDayId === endDayId ? [{ ...segment, fromNodeId, toNodeId, startDayId, endDayId }] : [];
    });
  });
}
